import { useEffect, useState } from "react";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { inr } from "@/lib/utils2";

export default function Gstr1() {
  const [period, setPeriod] = usePersistedState("period:gstr1", () => presetRange("this-month"));
  const [data, setData] = useState(null);

  const run = async (p) => {
    const range = p || period;
    const { data } = await api.get("/gstr1", { params: { date_from: range.from, date_to: range.to } });
    setData(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [period.from, period.to]);

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `gstr1-${period.from}-to-${period.to}.json`; a.click();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-6xl mx-auto" data-testid="gstr1-page">
      <PageHeader
        title="GSTR-1 Summary"
        subtitle="B2B invoices + HSN-wise summary for the selected period. Export JSON for offline filing tools."
        action={data && <Button onClick={downloadJson} data-testid="gstr1-download-btn" className="rounded-sm bg-brand-forest text-white"><Download className="h-4 w-4 mr-1.5" /> Download JSON</Button>}
      />

      <Card className="rounded-sm border-brand-line shadow-none p-5 mb-6">
        <PeriodPicker value={period} onChange={(p) => setPeriod({ from: p.from, to: p.to })}
          defaultPreset="this-month" testIdPrefix="gstr1-period" />
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPI label="Invoices" value={data.totals.invoice_count} />
            <KPI label="Taxable Value" value={inr(data.totals.taxable_value)} />
            <KPI label="CGST + SGST" value={inr(data.totals.cgst + data.totals.sgst)} />
            <KPI label="Total" value={inr(data.totals.total)} accent="text-brand-forest" />
          </div>

          <Card className="rounded-sm border-brand-line shadow-none mb-6 overflow-hidden">
            <div className="px-5 py-4 border-b border-brand-line">
              <div className="font-serif text-xl text-brand-ink">B2B Invoices</div>
              <div className="text-xs text-brand-muted">{data.b2b.length} invoice(s) in period</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                  <tr>
                    <th className="text-left px-3 py-2">CTIN (GSTIN)</th>
                    <th className="text-left px-3 py-2">Party</th>
                    <th className="text-left px-3 py-2">Invoice No</th>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-right px-3 py-2">Rate</th>
                    <th className="text-right px-3 py-2">Taxable</th>
                    <th className="text-right px-3 py-2">CGST</th>
                    <th className="text-right px-3 py-2">SGST</th>
                    <th className="text-right px-3 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.b2b.length === 0 && <tr><td colSpan={9} className="px-5 py-8 text-center text-brand-muted">No B2B invoices in this period.</td></tr>}
                  {data.b2b.map((b, i) => (
                    <tr key={i} className="border-t border-brand-line">
                      <td className="px-3 py-1.5 font-mono">{b.ctin || "—"}</td>
                      <td className="px-3 py-1.5 font-medium">{b.party_name}</td>
                      <td className="px-3 py-1.5 font-mono">{b.inv_no}</td>
                      <td className="px-3 py-1.5">{b.inv_dt}</td>
                      <td className="px-3 py-1.5 text-right">{b.items[0]?.rt}%</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{inr(b.items[0]?.txval)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{inr(b.items[0]?.camt)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{inr(b.items[0]?.samt)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{inr(b.val)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
            <div className="px-5 py-4 border-b border-brand-line">
              <div className="font-serif text-xl text-brand-ink">HSN-wise Summary</div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                <tr>
                  <th className="text-left px-3 py-2">HSN</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-right px-3 py-2">UQC</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Taxable</th>
                  <th className="text-right px-3 py-2">CGST</th>
                  <th className="text-right px-3 py-2">SGST</th>
                </tr>
              </thead>
              <tbody>
                {data.hsn.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-brand-muted">No HSN data.</td></tr>}
                {data.hsn.map((h, i) => (
                  <tr key={i} className="border-t border-brand-line">
                    <td className="px-3 py-1.5 font-mono">{h.hsn}</td>
                    <td className="px-3 py-1.5">{h.desc}</td>
                    <td className="px-3 py-1.5 text-right">{h.uqc}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{h.qty}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{inr(h.txval)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{inr(h.cgst)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{inr(h.sgst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <Card className="rounded-sm border-brand-line shadow-none p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">{label}</div>
      <div className={`font-serif text-2xl mt-2 tabular-nums ${accent || "text-brand-ink"}`}>{value}</div>
    </Card>
  );
}
